import { zodResolver } from '@hookform/resolvers/zod';
import { type Event, type QuoteDocument, QuoteLine, TimesheetDepartment } from '@kalos/kalos-rpc';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  Checkbox,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
  LoadingIcon,
  MoneyInput,
  Textarea,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import {
  Cross2Icon,
  ExclamationTriangleIcon,
  MinusIcon,
  Pencil1Icon,
  PlusIcon,
} from '@radix-ui/react-icons';
import { Lock } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useMediaQuery } from '../../../hooks/useMedia';
import {
  debounce,
  QuoteLineClientService,
  TimesheetDepartmentClientService,
} from '../../../tools/helpers';
import {
  getQuoteDocumentTaxCost,
  getQuoteDocumentTaxRate,
} from '../../../utils/quoteDocumentPricing';

// Schema for adding a service
const addServiceSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  price: z.coerce.number().min(0, 'Price must be 0 or greater'),
});

type AddServiceFormData = z.infer<typeof addServiceSchema>;

// Schema for editing a service
const editServiceSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  price: z.coerce.number().min(0, 'Price must be 0 or greater'),
});

type EditServiceFormData = z.infer<typeof editServiceSchema>;

// Local storage key and interface for temp proposal item
const TEMP_PROPOSAL_ITEM_KEY = 'TEMP_PROPOSAL_ITEM';

interface TempProposalItem {
  jobNumber: string;
  name: string;
  amount: number;
}

// Helper functions for localStorage operations
const saveTempProposalItem = (item: TempProposalItem) => {
  try {
    localStorage.setItem(TEMP_PROPOSAL_ITEM_KEY, JSON.stringify(item));
  } catch (error) {
    console.error('Error saving temp proposal item to localStorage:', error);
  }
};

const loadTempProposalItem = (): TempProposalItem | null => {
  try {
    const stored = localStorage.getItem(TEMP_PROPOSAL_ITEM_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error loading temp proposal item from localStorage:', error);
    return null;
  }
};

const clearTempProposalItem = () => {
  try {
    localStorage.removeItem(TEMP_PROPOSAL_ITEM_KEY);
  } catch (error) {
    console.error('Error clearing temp proposal item from localStorage:', error);
  }
};

interface Props {
  selectedServices: QuoteLine[];
  onServicesChange?: (services: QuoteLine[]) => void;
  onUpdateService?: (service: QuoteLine) => Promise<QuoteLine | undefined>;
  onDeleteService?: (service: QuoteLine) => Promise<void>;
  onAddService?: (service: QuoteLine) => Promise<QuoteLine | undefined>;
  serviceCallId?: string; // Job number for the service call
  onValidationChange?: (isValid: boolean) => void; // New prop to notify parent of validation state
  serviceItem?: Event; // Job context (department, etc.)
  /** When set, discount display and edits use the quote document (proposal source of truth). */
  quoteDocument?: QuoteDocument | null;
  onDiscountChange?: (discount: string, discountcost: string) => void; // Persists to quote document
  isProposalAdmin?: boolean; // Whether user can mark line items as non-negotiable (accepted)
}

export const ServicesStep: FC<Props> = ({
  selectedServices,
  onServicesChange,
  onUpdateService,
  onDeleteService,
  onAddService,
  serviceCallId,
  onValidationChange,
  serviceItem,
  quoteDocument,
  onDiscountChange,
  isProposalAdmin = false,
}) => {
  const discountPercentStr =
    quoteDocument?.id && quoteDocument.discount !== undefined
      ? quoteDocument.discount
      : serviceItem?.discount ?? '0';
  const discountCostStr =
    quoteDocument?.id && quoteDocument.discountcost !== undefined
      ? quoteDocument.discountcost
      : serviceItem?.discountcost ?? '0';
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [flatRates, setFlatRates] = useState<QuoteLine[]>([]);
  const [filteredFlatRates, setFilteredFlatRates] = useState<QuoteLine[]>([]);
  const [departments, setDepartments] = useState<TimesheetDepartment[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<number>(
    serviceItem?.departmentId || 0,
  ); // Initialize to event's department, or 0 for "Quote Only"
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasLoadedFromStorage, setHasLoadedFromStorage] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [editDiscountValue, setEditDiscountValue] = useState<string>('');
  const [editDiscountAmount, setEditDiscountAmount] = useState<string>('');
  const [isFlatDiscount, setIsFlatDiscount] = useState(false);
  const [isSavingDiscount, setIsSavingDiscount] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    serviceId: number | null;
    serviceName: string;
  }>({
    isOpen: false,
    serviceId: null,
    serviceName: '',
  });
  const [markingItemId, setMarkingItemId] = useState<number | null>(null);

  // State for unified edit dialog
  const [editDialog, setEditDialog] = useState<{
    isOpen: boolean;
    service: QuoteLine | undefined;
    serviceId: number | null;
  }>({
    isOpen: false,
    service: undefined,
    serviceId: null,
  });

  // State for mobile actions accordion
  const [mobileActions, setMobileActions] = useState<{
    isOpen: boolean;
    serviceId: number | null;
  }>({
    isOpen: false,
    serviceId: null,
  });

  // Use media query to determine if we're on mobile
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Function to get initial form values based on localStorage
  const getInitialFormValues = (): AddServiceFormData => {
    const tempItem = loadTempProposalItem();

    // Only load from storage if job numbers match and we haven't loaded yet
    if (tempItem && tempItem.jobNumber === (serviceCallId || '') && !hasLoadedFromStorage) {
      return {
        description: tempItem.name,
        price: tempItem.amount,
      };
    }

    // If job numbers don't match, clear the storage
    if (tempItem && tempItem.jobNumber !== (serviceCallId || '')) {
      clearTempProposalItem();
    }

    return {
      description: '',
      price: 0,
    };
  };

  // Form for adding custom service
  const form = useForm<AddServiceFormData>({
    resolver: zodResolver(addServiceSchema),
    defaultValues: getInitialFormValues(),
  });

  // Form for editing service
  const editForm = useForm<EditServiceFormData>({
    resolver: zodResolver(editServiceSchema),
    defaultValues: {
      description: '',
      price: 0,
    },
  });

  // Mark that we've loaded from storage on first render
  useState(() => {
    setHasLoadedFromStorage(true);
  });

  // Save to localStorage when form values change (but only if form has been touched)
  const saveFormToStorage = useCallback(() => {
    if (!form.formState.isDirty || !serviceCallId) return;

    const values = form.getValues();
    const tempItem: TempProposalItem = {
      jobNumber: serviceCallId,
      name: values.description,
      amount: values.price,
    };

    // Only save if there's actual content
    if (values.description.trim() || values.price > 0) {
      saveTempProposalItem(tempItem);
    }
  }, [form, serviceCallId]);

  // Save to storage whenever watched values change (debounced)
  const debouncedSave = useMemo(() => debounce(saveFormToStorage, 500), [saveFormToStorage]);

  // Trigger save when form values change
  useState(() => {
    if (form.formState.isDirty) {
      debouncedSave();
    }
  });

  // Calculate total price based on quantity and adjustment
  const calculateTotalPrice = (service: QuoteLine): number => {
    const quantity = service.quotedQuantity || 1;
    const price = parseFloat(service.adjustment || '0');
    return price * quantity;
  };

  // Check if validation passes - at least one service with valid price
  const hasValidServices = useMemo(() => {
    return selectedServices.length === 0 ? false : true;
  }, [selectedServices]);

  // Notify parent of validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(hasValidServices);
    }
  }, [hasValidServices, onValidationChange]);

  // Handle quantity change with debounce
  const updateServiceQuantity = debounce((service: QuoteLine, newQuantity: number) => {
    // Ensure quantity is at least 1
    const validQuantity = Math.max(1, newQuantity);

    // Create a new QuoteLine with updated quantity
    const updatedService = QuoteLine.create({
      ...service,
      quotedQuantity: validQuantity,
    });

    if (onUpdateService) {
      // Use the direct mutation handler which updates cache immediately
      onUpdateService(updatedService).catch((err) => {
        console.error('Error updating service quantity:', err);
        toast({
          title: 'Error updating quantity',
          variant: 'destructive',
        });
      });
    } else if (onServicesChange) {
      // Fallback to batch update approach if no direct mutation
      const updatedServices = selectedServices.map((s) =>
        s.id === service.id ? updatedService : s,
      );
      onServicesChange(updatedServices);
    }
  }, 200); // Reduced debounce time for better responsiveness

  // Load departments
  const loadDepartments = useCallback(async () => {
    try {
      const departmentRequest = TimesheetDepartment.create();
      departmentRequest.isActive = 1;
      const departmentList = await TimesheetDepartmentClientService.BatchGet(departmentRequest);
      if (departmentList?.results) {
        // Make sure each department has an id and description property that we can use
        const validDepartments = departmentList.results.filter(
          (dept) => dept.id && dept.description,
        );
        setDepartments(validDepartments);
      }
    } catch (error) {
      console.error('Error loading departments:', error);
      toast({
        title: 'Error loading departments',
        variant: 'destructive',
      });
    }
  }, []);

  const filterItems = useCallback((items: QuoteLine[], search: string, department: number) => {
    const filtered = items.filter((item) => {
      // Filter by search term
      const matchesSearch =
        !search.trim() || item.description.toLowerCase().includes(search.toLowerCase());

      // Filter by department
      const itemDepartments = item.departments ? item.departments.split(',').map(Number) : [];
      const matchesDepartment = itemDepartments.includes(department);

      return matchesSearch && matchesDepartment;
    });

    setFilteredFlatRates(filtered);
  }, []);

  // Load flat rates (run only once on mount)
  const loadFlatRates = useCallback(async () => {
    setLoading(true);
    try {
      const quoteRequest = QuoteLine.create();
      quoteRequest.isActive = 1;
      quoteRequest.isFlatrate = '1';
      quoteRequest.pageNumber = 0;
      quoteRequest.withoutLimit = true;

      const results = (await QuoteLineClientService.BatchGet(quoteRequest))?.results || [];

      // Filter out flat rate items that have been assigned to specific jobs
      const catalogOnlyResults = results.filter(
        (item) => !item.jobNumber || item.jobNumber.trim() === '',
      );

      // Sort by description
      const sortedResults = [...catalogOnlyResults].sort((a, b) =>
        a.description.localeCompare(b.description),
      );

      setFlatRates(sortedResults);
      // Filtering will be handled by the separate effect that watches searchTerm & selectedDepartment.
    } catch (error) {
      console.error('Error loading flat rate items:', error);
      toast({
        title: 'Error loading service catalog',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    loadDepartments();
    loadFlatRates();
  }, [loadDepartments, loadFlatRates]);

  // Filter when search term or department changes
  useEffect(() => {
    filterItems(flatRates, searchTerm, selectedDepartment);
  }, [filterItems, flatRates, searchTerm, selectedDepartment]);

  // Get departments that have items
  const getDepartmentsWithItems = useCallback(() => {
    // Get all department IDs that have at least one item
    const departmentIdsWithItems = new Set<number>();

    flatRates.forEach((item) => {
      const itemDepartments = item.departments ? item.departments.split(',').map(Number) : [];
      itemDepartments.forEach((deptId) => {
        departmentIdsWithItems.add(deptId);
      });
    });

    // Filter departments to only those that have items
    const filteredDepartments = departments.filter((dept) => departmentIdsWithItems.has(dept.id));

    // Add "Quote Only" (department 0) if there are items with department 0
    if (departmentIdsWithItems.has(0)) {
      // Create a pseudo-department for department 0
      const quoteOnlyDept = TimesheetDepartment.create({
        id: 0,
        description: 'Quote Only',
        isActive: 1,
      });
      filteredDepartments.unshift(quoteOnlyDept); // Add at the beginning
    }

    return filteredDepartments;
  }, [departments, flatRates]);

  const departmentsWithItems = useMemo(() => getDepartmentsWithItems(), [getDepartmentsWithItems]);

  // Add service from catalog
  const handleAddCatalogService = async (service: QuoteLine) => {
    // Check if service already added by id
    const exists = selectedServices.some(
      (s) =>
        s.description === service.description &&
        parseFloat(s.adjustment || '0') === parseFloat(service.adjustment || '0'),
    );

    if (exists) {
      toast({
        title: 'Service already added',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Create a new QuoteLine based on the catalog item
      const newService = QuoteLine.create({
        description: service.description,
        adjustment: service.adjustment,
        isFlatrate: '0', // Set to 0 instead of 1 to allow editing
        jobNumber: serviceCallId || '',
        isActive: 1,
        warranty: 2, // Default warranty value as per old implementation
        quotedQuantity: 1, // Default quantity
        excludeInvoiceDiscount: service.excludeInvoiceDiscount,
      });

      let savedService: QuoteLine | undefined;

      if (onAddService) {
        // Use the direct mutation handler which updates cache immediately
        savedService = await onAddService(newService);
      } else {
        // Legacy path - save to server and wait for response
        savedService = await QuoteLineClientService.Create(newService);

        if (savedService && onServicesChange) {
          // Set initial quantity
          savedService.quotedQuantity = 1;

          // Manual UI update for the fallback path
          onServicesChange([...selectedServices, savedService]);
        }
      }

      if (savedService) {
        toast({
          title: 'Service added',
          variant: 'success',
        });
      } else {
        toast({
          title: 'Failed to add service',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error adding catalog service:', error);
      toast({
        title: 'Error adding service',
        variant: 'destructive',
      });
    }
  };

  // Handle custom service submission
  const handleAddCustomService = async (data: AddServiceFormData) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // Ensure price is a number
      const price = typeof data.price === 'string' ? parseFloat(data.price) : data.price;

      // Create a new QuoteLine for the custom service
      const newQuoteLine = QuoteLine.create({
        description: data.description,
        adjustment: price > 0 ? price.toFixed(2) : '0.00',
        isFlatrate: '0', // This is a custom service, not a flat rate
        jobNumber: serviceCallId || '',
        isActive: 1,
        warranty: 2, // Default warranty value as per old implementation
        quotedQuantity: 1, // Default quantity
      });

      try {
        // First save the QuoteLine to get the server-generated ID
        let savedQuoteLine: QuoteLine | undefined;

        if (onAddService) {
          // Use the direct mutation handler which updates cache immediately
          savedQuoteLine = await onAddService(newQuoteLine);
        } else {
          // Legacy path
          savedQuoteLine = await QuoteLineClientService.Create(newQuoteLine);

          // Manual UI update if we have onServicesChange
          if (savedQuoteLine && onServicesChange) {
            savedQuoteLine.quotedQuantity = 1;
            onServicesChange([...selectedServices, savedQuoteLine]);
          }
        }

        if (!savedQuoteLine && onServicesChange) {
          // If save failed but we need to update UI
          onServicesChange([...selectedServices, newQuoteLine]);
          console.error('Failed to get saved QuoteLine from server');
        }
      } catch (error) {
        // If there was an error saving to server but we need to update UI
        if (onServicesChange) {
          onServicesChange([...selectedServices, newQuoteLine]);
        }
        console.error('Error saving QuoteLine to server:', error);
      }

      // Clear the form and localStorage on successful add
      form.reset({
        description: '',
        price: 0,
      });
      clearTempProposalItem();

      toast({
        title: 'Custom service added',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error adding custom service:', error);
      toast({
        title: 'Error adding custom service',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove service
  const handleRemoveService = async (id: number) => {
    try {
      // Find the service to remove
      const serviceToRemove = selectedServices.find((s) => s.id === id);
      if (!serviceToRemove) {
        console.error('Service not found:', id);
        return;
      }

      if (onDeleteService && serviceToRemove.id > 0) {
        // Use the direct mutation handler which updates cache immediately
        await onDeleteService(serviceToRemove);
      } else {
        // For local-only items or fallback
        // Update UI immediately
        if (onServicesChange) {
          const updatedServices = selectedServices.filter((service) => service.id !== id);
          onServicesChange(updatedServices);
        }

        // Delete from server if it has an ID and no delete handler
        if (serviceToRemove.id > 0 && !onDeleteService) {
          await QuoteLineClientService.Delete(serviceToRemove);
        }
      }

      toast({
        title: 'Service removed',
        variant: 'default',
      });
    } catch (error) {
      console.error('Error removing service:', error);

      toast({
        title: 'Error removing service',
        variant: 'destructive',
      });
    }
  };

  // Initiate delete process - shows confirmation for approved services
  const initiateDelete = (serviceId: number) => {
    const service = selectedServices.find((s) => s.id === serviceId);
    if (!service) return;

    if (isServiceApproved(service)) {
      // Show confirmation dialog for approved services
      setDeleteConfirmation({
        isOpen: true,
        serviceId: serviceId,
        serviceName: service.description,
      });
    } else {
      // Direct delete for non-approved services
      handleRemoveService(serviceId);
    }
  };

  // Confirm delete for approved services
  const confirmDelete = async () => {
    if (deleteConfirmation.serviceId) {
      await handleRemoveService(deleteConfirmation.serviceId);
      setDeleteConfirmation({ isOpen: false, serviceId: null, serviceName: '' });
    }
  };

  // Cancel delete confirmation
  const cancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, serviceId: null, serviceName: '' });
  };

  // Handle edit discount button click
  const handleEditDiscount = useCallback(() => {
    setEditDiscountValue(discountPercentStr || '0');
    setEditDiscountAmount(discountCostStr || '0');

    // Auto-tick flat rate if discount percentage is 0 but discount amount > 0
    const discountPercentValue = parseFloat(discountPercentStr || '0');
    const discountAmountValue = parseFloat(discountCostStr || '0');
    const shouldUseFlatRate = discountPercentValue === 0 && discountAmountValue > 0;
    setIsFlatDiscount(shouldUseFlatRate);

    setEditingDiscount(true);
  }, [discountPercentStr, discountCostStr]);

  // Handle cancel discount edit
  const handleCancelDiscountEdit = useCallback(() => {
    setEditingDiscount(false);
    setEditDiscountValue('');
    setEditDiscountAmount('');
    setIsFlatDiscount(false);
  }, []);

  // Handle save discount
  const handleSaveDiscount = useCallback(async () => {
    if (!onDiscountChange) {
      return;
    }

    try {
      let discountPercentValue: number;
      let discountAmountValue: string;

      if (isFlatDiscount) {
        // Flat discount: validate amount
        const flatAmount = parseFloat(editDiscountAmount || '0');
        if (isNaN(flatAmount) || flatAmount < 0) {
          toast({
            title: 'Invalid discount',
            description: 'Discount amount must be 0 or greater',
            variant: 'destructive',
          });
          return;
        }
        discountPercentValue = 0;
        discountAmountValue = flatAmount.toFixed(2);
      } else {
        // Percentage discount: validate percentage
        const percentValue = parseFloat(editDiscountValue || '0');
        if (isNaN(percentValue) || percentValue < 0 || percentValue > 100) {
          toast({
            title: 'Invalid discount',
            description: 'Discount must be between 0 and 100%',
            variant: 'destructive',
          });
          return;
        }
        discountPercentValue = percentValue;
        // For percentage discounts on quotes, we don't save discountcost since total isn't fixed yet
        discountAmountValue = '0';
      }

      setIsSavingDiscount(true);

      await onDiscountChange(discountPercentValue.toString(), discountAmountValue);

      setEditingDiscount(false);
      setEditDiscountValue('');
      setEditDiscountAmount('');
      setIsFlatDiscount(false);

      toast({
        title: 'Discount saved',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error saving discount:', error);
      toast({
        title: 'Error saving discount',
        variant: 'destructive',
      });
    } finally {
      setIsSavingDiscount(false);
    }
  }, [editDiscountValue, editDiscountAmount, isFlatDiscount, onDiscountChange]);

  // Calculate totals
  const subtotal = selectedServices.reduce((sum, service) => {
    return sum + calculateTotalPrice(service);
  }, 0);

  const excludedSubtotal = selectedServices.reduce((sum, service) => {
    return service.excludeInvoiceDiscount === 1 ? sum + calculateTotalPrice(service) : sum;
  }, 0);

  // Calculate discount amount - use flat amount if percentage is 0 and discountcost exists
  const discountPercentValue = parseFloat(discountPercentStr || '0');
  const discountCostValue = parseFloat(discountCostStr || '0');
  const discountableBase = subtotal - excludedSubtotal;
  const discountAmount =
    discountPercentValue > 0 ? discountableBase * (discountPercentValue / 100) : discountCostValue;
  const afterDiscount = subtotal - discountAmount;
  const taxRate = quoteDocument?.id ? getQuoteDocumentTaxRate(quoteDocument) : 0;
  const taxCostFlat = quoteDocument?.id ? getQuoteDocumentTaxCost(quoteDocument) : 0;
  const taxAmount = taxRate > 0 ? afterDiscount * (taxRate / 100) : taxCostFlat;
  const total = afterDiscount + taxAmount;

  // Get status based on service properties
  const getServiceStatus = (service: QuoteLine): 'accepted' | 'not-accepted' => {
    // Check if service has been accepted
    return service.quoteStatus === 1 ? 'accepted' : 'not-accepted';
  };

  // Helper function to check if service is approved (status == 1)
  const isServiceApproved = (service: QuoteLine): boolean => {
    return service.quoteStatus === 1;
  };

  const StatusIcon = ({ status }: { status: 'accepted' | 'not-accepted' }) => {
    return (
      <div className="flex items-center justify-center">
        {status === 'accepted' ? (
          <div className="h-2 w-2 rounded-full bg-green-500"></div>
        ) : (
          <div className="h-2 w-2 rounded-full bg-red-500"></div>
        )}
      </div>
    );
  };

  const StatusLegend = () => (
    <div className="flex items-center gap-3 text-xs text-gray-600">
      <div className="flex items-center gap-1">
        <div className="h-2 w-2 rounded-full bg-green-500"></div>
        <span>Accepted</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="h-2 w-2 rounded-full bg-red-500"></div>
        <span>Not Accepted</span>
      </div>
    </div>
  );

  // Open unified edit dialog
  const openEditDialog = (serviceId: number) => {
    const service = selectedServices.find((s) => s.id === serviceId);
    if (!service) return;

    setEditDialog({
      isOpen: true,
      service,
      serviceId: serviceId,
    });

    // Set form values
    editForm.reset({
      description: service.description,
      price: parseFloat(service.adjustment || '0'),
    });
  };

  // Close unified edit dialog
  const closeEditDialog = () => {
    setEditDialog({
      isOpen: false,
      service: undefined,
      serviceId: null,
    });
    editForm.reset();
  };

  // Save changes from unified edit dialog
  const saveEditedService = async (data: EditServiceFormData, service: QuoteLine) => {
    console.log('saveEditedService called with:', data, service);

    try {
      // Create updated service with field mask
      const updatedService = QuoteLine.create({
        ...service,
        id: service.id,
        description: data.description.trim(),
        adjustment: data.price.toFixed(2),
        fieldMask: ['Description', 'Adjustment'],
      });

      if (onUpdateService) {
        // Use the direct mutation handler
        await onUpdateService(updatedService);
      } else {
        // Fallback: update via API and manual UI update
        await QuoteLineClientService.Update(updatedService);

        if (onServicesChange) {
          const updatedServices = selectedServices.map((s) =>
            s.id === service.id ? updatedService : s,
          );
          onServicesChange(updatedServices);
        }
      }

      closeEditDialog();

      toast({
        title: 'Service updated successfully',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error updating service:', error);
      toast({
        title: 'Error updating service',
        variant: 'destructive',
      });
    }
  };

  // Mark as non-negotiable (ProposalAdmin only - sets status to accepted)
  const handleMarkAsNonNegotiable = useCallback(
    async (service: QuoteLine) => {
      if (!onUpdateService) return;
      setMarkingItemId(service.id);
      try {
        await onUpdateService(
          QuoteLine.create({
            ...service,
            quoteStatus: 1,
            fieldMask: ['QuoteStatus'],
          }),
        );
      } catch {
        toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
      } finally {
        setMarkingItemId(null);
      }
    },
    [onUpdateService],
  );

  // Toggle excludeInvoiceDiscount on a line item
  const handleToggleExclude = useCallback(
    async (service: QuoteLine, excluded: boolean) => {
      if (!onUpdateService) return;
      try {
        await onUpdateService(
          QuoteLine.create({
            ...service,
            excludeInvoiceDiscount: excluded ? 1 : 0,
            fieldMask: ['ExcludeInvoiceDiscount'],
          }),
        );
      } catch {
        toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
      }
    },
    [onUpdateService],
  );

  // Open mobile actions accordion
  const openMobileActions = (serviceId: number) => {
    setMobileActions({
      isOpen: true,
      serviceId,
    });
  };

  // Close mobile actions accordion
  const closeMobileActions = () => {
    setMobileActions({
      isOpen: false,
      serviceId: null,
    });
  };

  // Mobile compact table rendering for selected services
  const renderMobileServiceList = () => {
    return (
      <div className="space-y-3">
        {selectedServices.map((service) => {
          const quantity = service.quotedQuantity || 1;
          const basePrice = parseFloat(service.adjustment || '0');
          const totalPrice = basePrice * quantity;
          const status = getServiceStatus(service);
          const isApproved = isServiceApproved(service);

          return (
            <div key={service.id} className="rounded border bg-white p-2">
              <div className="flex items-start justify-between">
                <div className="flex-1 overflow-hidden pr-2">
                  <div className="mb-1">
                    <div
                      className="min-w-0 flex-1 break-words text-sm font-medium leading-tight"
                      style={{ wordBreak: 'break-word', hyphens: 'auto' }}
                      title={service.description}
                    >
                      {service.description}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">Qty:</span>
                      <div className="flex items-center">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-5 w-5 bg-white disabled:opacity-30"
                          onClick={() => updateServiceQuantity(service, quantity - 1)}
                          disabled={isApproved}
                        >
                          <MinusIcon className="h-2 w-2" />
                        </Button>
                        <span className="mx-1 min-w-[20px] text-center text-xs">{quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-5 w-5 bg-white disabled:opacity-30"
                          onClick={() => updateServiceQuantity(service, quantity + 1)}
                          disabled={isApproved}
                        >
                          <PlusIcon className="h-2 w-2" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="whitespace-nowrap text-xs">
                        ${basePrice.toFixed(2)} × {quantity} ={' '}
                        <span className="font-bold">${totalPrice.toFixed(2)}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <StatusIcon status={status} />
                  {isProposalAdmin && !isApproved && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 flex-shrink-0 bg-white p-0 hover:bg-gray-50"
                            onClick={() => handleMarkAsNonNegotiable(service)}
                            disabled={markingItemId === service.id}
                          >
                            {markingItemId === service.id ? (
                              <LoadingIcon className="h-3 w-3" width={12} height={12} />
                            ) : (
                              <Lock className="h-3 w-3 text-gray-500" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Non-Negotiable</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 flex-shrink-0 bg-white p-0 hover:bg-gray-50"
                    onClick={() => openMobileActions(service.id)}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </Button>
                </div>
              </div>
            </div>
          );
        })}

        {/* Totals Section */}
        <div className="space-y-2 rounded border-t-2 bg-gray-50 p-3">
          <div className="flex items-center justify-between text-sm">
            <span>Subtotal:</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-base font-medium">Discount:</span>
            {!editingDiscount ? (
              <div className="flex items-center gap-2">
                <span className="text-base font-medium">
                  {discountPercentValue > 0
                    ? `${discountPercentValue}%`
                    : discountCostValue > 0
                      ? `$${discountCostValue.toFixed(2)} (Flat)`
                      : '0%'}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={handleEditDiscount}
                  disabled={isSavingDiscount}
                >
                  <Pencil1Icon className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={isFlatDiscount}
                    onCheckedChange={(checked) => setIsFlatDiscount(!!checked)}
                    disabled={isSavingDiscount}
                    className="border-gray-300 bg-white data-[state=checked]:border-black data-[state=checked]:bg-black"
                  />
                  <span className="text-xs">Flat</span>
                </div>
                <div className="flex items-center gap-1">
                  {isFlatDiscount ? (
                    <>
                      <span className="text-sm">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editDiscountAmount}
                        onChange={(e) => setEditDiscountAmount(e.target.value)}
                        className="h-8 w-20 bg-white text-right text-sm"
                        disabled={isSavingDiscount}
                        autoFocus
                      />
                    </>
                  ) : (
                    <>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={editDiscountValue}
                        onChange={(e) => setEditDiscountValue(e.target.value)}
                        className="h-8 w-16 bg-white text-right text-sm"
                        disabled={isSavingDiscount}
                        autoFocus
                      />
                      <span className="text-xs">%</span>
                    </>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 flex-shrink-0 p-1 text-green-600 hover:text-green-700"
                  onClick={handleSaveDiscount}
                  disabled={isSavingDiscount}
                >
                  ✓
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 flex-shrink-0 p-1 text-red-600 hover:text-red-700"
                  onClick={handleCancelDiscountEdit}
                  disabled={isSavingDiscount}
                >
                  ✗
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Discount Amount:</span>
            <span>-${discountAmount.toFixed(2)}</span>
          </div>
          {(taxRate > 0 || taxCostFlat > 0) && (
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>{taxRate > 0 ? `Tax (${taxRate}%):` : 'Tax (flat):'}</span>
              <span>${taxAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex items-center justify-between border-t pt-2 font-bold">
            <span>Total:</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Desktop table rendering for selected services
  const renderDesktopServiceTable = () => {
    return (
      <div className="overflow-hidden rounded-md border border-gray-200">
        {/* Table header */}
        <div
          className="grid-cols-13 grid border-b border-gray-300 bg-gray-100 px-2 py-2 text-xs font-semibold text-gray-700"
          style={{ gridTemplateColumns: '3fr 2fr 2fr 2fr 1fr 1fr 1fr' }}
        >
          <div className="text-left">Description</div>
          <div className="text-center">Qty</div>
          <div className="text-left">Price</div>
          <div className="text-left">Total</div>
          <div className="text-center">Excl.</div>
          <div className="text-center">Status</div>
          <div className="text-center">Actions</div>
        </div>

        {/* Scrollable content */}
        <div className="max-h-[50vh] overflow-y-auto">
          {selectedServices.map((service) => {
            const quantity = service.quotedQuantity || 1;
            const basePrice = parseFloat(service.adjustment || '0');
            const totalPrice = basePrice * quantity;
            const status = getServiceStatus(service);
            const isApproved = isServiceApproved(service);

            return (
              <div
                key={service.id}
                className="grid border-b border-gray-200 bg-white px-2 py-2 text-sm hover:bg-gray-50"
                style={{ gridTemplateColumns: '3fr 2fr 2fr 2fr 1fr 1fr 1fr' }}
              >
                <div className="overflow-hidden py-1 pr-1">
                  <div
                    className="min-w-0 flex-1 break-words text-xs font-medium leading-tight"
                    style={{ wordBreak: 'break-word', hyphens: 'auto' }}
                    title={service.description}
                  >
                    {service.description}
                  </div>
                </div>
                <div className="flex items-center justify-center py-1">
                  <div className="flex items-center justify-center">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-5 w-5 bg-white disabled:opacity-30"
                      onClick={() => updateServiceQuantity(service, quantity - 1)}
                      disabled={isApproved}
                    >
                      <MinusIcon className="h-2 w-2" />
                    </Button>
                    <span className="mx-1 min-w-[20px] text-center text-xs">{quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-5 w-5 bg-white disabled:opacity-30"
                      onClick={() => updateServiceQuantity(service, quantity + 1)}
                      disabled={isApproved}
                    >
                      <PlusIcon className="h-2 w-2" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-start py-1 pr-1">
                  <span className="whitespace-nowrap text-left text-xs font-medium">
                    ${basePrice.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-start py-1">
                  <span className="whitespace-nowrap text-left text-xs font-semibold">
                    ${totalPrice.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-center py-1">
                  <Checkbox
                    checked={service.excludeInvoiceDiscount === 1}
                    onCheckedChange={(checked) => handleToggleExclude(service, !!checked)}
                    disabled={isApproved || !onUpdateService}
                    className="border-gray-300 bg-white data-[state=checked]:border-black data-[state=checked]:bg-black"
                  />
                </div>
                <div className="flex items-center justify-center py-1">
                  <StatusIcon status={status} />
                </div>
                <div className="flex items-center justify-center gap-1 py-1">
                  {isProposalAdmin && !isApproved && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 flex-shrink-0 bg-white p-0 hover:bg-gray-50"
                            onClick={() => handleMarkAsNonNegotiable(service)}
                            disabled={markingItemId === service.id}
                          >
                            {markingItemId === service.id ? (
                              <LoadingIcon className="h-3 w-3" width={12} height={12} />
                            ) : (
                              <Lock className="h-3 w-3 text-gray-500" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Non-Negotiable</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 flex-shrink-0 bg-white p-0 hover:bg-gray-50 disabled:opacity-30"
                    onClick={() => openEditDialog(service.id)}
                    disabled={isApproved}
                  >
                    <Pencil1Icon className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 flex-shrink-0 bg-white p-0 hover:bg-gray-50"
                    onClick={() => initiateDelete(service.id)}
                  >
                    <Cross2Icon className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer with Discount */}
        <div className="border-t-2 border-gray-400 bg-gray-50">
          {/* Subtotal Row */}
          <div
            className="grid border-b border-gray-300 px-2 py-2 text-sm text-black"
            style={{ gridTemplateColumns: '3fr 2fr 2fr 2fr 1fr 1fr 1fr' }}
          >
            <div></div>
            <div></div>
            <div className="flex items-center justify-start text-left">Subtotal:</div>
            <div className="flex items-center justify-start text-left font-medium">
              ${subtotal.toFixed(2)}
            </div>
            <div></div>
            <div></div>
            <div></div>
          </div>

          {/* Discount Row */}
          <div
            className="grid border-b border-gray-300 px-2 py-2 text-sm text-black"
            style={{ gridTemplateColumns: '3fr 2fr 2fr 2fr 1fr 1fr 1fr' }}
          >
            <div></div>
            <div></div>
            <div className="flex items-center justify-start text-left">
              <span className="text-base font-medium">Discount:</span>
            </div>
            <div className="col-span-4 flex items-center justify-start gap-1 text-left">
              {!editingDiscount ? (
                <>
                  <span className="text-base font-medium">
                    {discountPercentValue > 0
                      ? `${discountPercentValue}%`
                      : discountCostValue > 0
                        ? `$${discountCostValue.toFixed(2)} (Flat)`
                        : '0%'}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={handleEditDiscount}
                    disabled={isSavingDiscount}
                  >
                    <Pencil1Icon className="h-3 w-3" />
                  </Button>
                </>
              ) : (
                <div className="flex items-center gap-1">
                  <Checkbox
                    checked={isFlatDiscount}
                    onCheckedChange={(checked) => setIsFlatDiscount(!!checked)}
                    disabled={isSavingDiscount}
                    className="border-gray-300 bg-white data-[state=checked]:border-black data-[state=checked]:bg-black"
                  />
                  <span className="text-xs">Flat</span>
                  {isFlatDiscount ? (
                    <>
                      <span className="text-sm">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editDiscountAmount}
                        onChange={(e) => setEditDiscountAmount(e.target.value)}
                        className="h-8 w-20 bg-white text-right text-sm"
                        disabled={isSavingDiscount}
                        autoFocus
                      />
                    </>
                  ) : (
                    <>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={editDiscountValue}
                        onChange={(e) => setEditDiscountValue(e.target.value)}
                        className="h-8 w-16 bg-white text-right text-sm"
                        disabled={isSavingDiscount}
                        autoFocus
                      />
                      <span className="text-xs">%</span>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 flex-shrink-0 p-1 text-green-600 hover:text-green-700"
                    onClick={handleSaveDiscount}
                    disabled={isSavingDiscount}
                  >
                    ✓
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 flex-shrink-0 p-1 text-red-600 hover:text-red-700"
                    onClick={handleCancelDiscountEdit}
                    disabled={isSavingDiscount}
                  >
                    ✗
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Discount Amount Row */}
          <div
            className="grid border-b border-gray-300 px-2 py-2 text-sm text-gray-600"
            style={{ gridTemplateColumns: '3fr 2fr 2fr 2fr 1fr 1fr 1fr' }}
          >
            <div></div>
            <div></div>
            <div className="flex items-center justify-start text-left">Discount Amount:</div>
            <div className="flex items-center justify-start text-left">
              -${discountAmount.toFixed(2)}
            </div>
            <div></div>
            <div></div>
            <div></div>
          </div>

          {(taxRate > 0 || taxCostFlat > 0) && (
            <div
              className="grid border-b border-gray-300 px-2 py-2 text-sm text-gray-600"
              style={{ gridTemplateColumns: '3fr 2fr 2fr 2fr 1fr 1fr 1fr' }}
            >
              <div></div>
              <div></div>
              <div className="flex items-center justify-start text-left">
                {taxRate > 0 ? `Tax (${taxRate}%):` : 'Tax (flat):'}
              </div>
              <div className="flex items-center justify-start text-left">
                ${taxAmount.toFixed(2)}
              </div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          )}

          {/* Total Row */}
          <div
            className="grid px-2 py-2 font-bold text-black"
            style={{ gridTemplateColumns: '3fr 2fr 2fr 2fr 1fr 1fr 1fr' }}
          >
            <div></div>
            <div></div>
            <div className="flex items-center justify-start text-left text-sm">Total:</div>
            <div className="flex items-center justify-start text-left text-base">
              ${total.toFixed(2)}
            </div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
      {/* Selected Services Section */}
      <div className="lg:col-span-2">
        <Card className="bg-white p-3">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Selected Services</h2>
            <StatusLegend />
          </div>

          {/* Warning for approved services */}
          {selectedServices.some(isServiceApproved) && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">Approved Services Detected</h3>
                  <div className="mt-1 text-sm text-amber-700">
                    Some services have been approved and cannot be edited. You can still delete
                    them, but this will require confirmation.
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedServices.length === 0 ? (
            <p className="py-2 text-center text-sm text-gray-500">
              No services selected yet. Select services from the catalog on the right.
            </p>
          ) : isMobile ? (
            renderMobileServiceList()
          ) : (
            renderDesktopServiceTable()
          )}

          {/* Add Custom Service - Responsive layout */}
          <div className="mt-3 border-t pt-3">
            <h3 className="mb-1 text-sm font-medium">Add Custom Service</h3>
            <FormContext {...form}>
              <form
                onSubmit={form.handleSubmit(
                  (data) => {
                    handleAddCustomService(data);
                  },
                  (errors) => {
                    console.error('Form validation failed:', errors);
                    toast({
                      title: 'Please fix the form errors',
                      variant: 'destructive',
                    });
                  },
                )}
                className="grid grid-cols-1 gap-3 md:grid-cols-12"
              >
                <div className="md:col-span-8">
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem className="mb-0">
                        <FormLabel className="text-xs">Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter service description"
                            className="h-20 bg-white text-sm"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              // Trigger save to localStorage after form state updates
                              setTimeout(() => {
                                if (form.formState.isDirty) {
                                  debouncedSave();
                                }
                              }, 0);
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="md:col-span-4">
                  <FormField
                    control={form.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem className="mb-0">
                        <FormLabel className="text-xs">Price</FormLabel>
                        <FormControl>
                          <MoneyInput
                            placeholder="0.00"
                            className="h-10 bg-white text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value;
                              const numValue = value === '' ? 0 : Number(value);
                              field.onChange(numValue);
                              // Trigger save to localStorage after form state updates
                              setTimeout(() => {
                                if (form.formState.isDirty) {
                                  debouncedSave();
                                }
                              }, 0);
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="mt-0 md:col-span-12">
                  <Button
                    type="submit"
                    className="h-10 w-full"
                    disabled={!form.formState.isValid || isSubmitting}
                    size="sm"
                  >
                    {isSubmitting ? 'Adding...' : 'Add'}
                  </Button>
                </div>
              </form>
            </FormContext>
          </div>
        </Card>
      </div>

      {/* Service Catalog Section */}
      <div>
        <Card className="h-full bg-white p-3">
          <h2 className="mb-2 text-lg font-semibold">Service Catalog</h2>

          {/* Search and Filter */}
          <div className="mb-2 space-y-2">
            <Input
              ignoreScrolling
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white text-sm"
            />

            <div className="flex flex-wrap gap-1">
              <div className="flex max-w-full flex-wrap gap-1">
                {departmentsWithItems.map((dept) => (
                  <Button
                    key={dept.id}
                    variant={selectedDepartment === dept.id ? 'default' : 'outline'}
                    onClick={() => setSelectedDepartment(dept.id)}
                    size="sm"
                    className={`h-6 px-2 text-xs ${selectedDepartment !== dept.id ? 'bg-white' : ''}`}
                  >
                    {dept.description}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Catalog Items - Slightly extended height */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-6">
              <div className="mb-2 flex items-center justify-center">
                <svg
                  className="animate-spin text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 20 20"
                >
                  <circle
                    cx="10"
                    cy="10"
                    r="8.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeOpacity=".2"
                  />
                  <circle
                    cx="10"
                    cy="10"
                    r="8.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeDasharray="13.3525 40.0575"
                  />
                </svg>
              </div>
              <div className="text-sm text-gray-500">Loading catalog items...</div>
            </div>
          ) : filteredFlatRates.length === 0 ? (
            <div className="py-4 text-center text-sm text-gray-500">
              No matching items found. Try adjusting your search or filters.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto overflow-x-hidden rounded border">
              {filteredFlatRates.map((item, index) => {
                // Check if this service is already added
                const isAlreadyAdded = selectedServices.some(
                  (s) =>
                    s.description === item.description &&
                    parseFloat(s.adjustment || '0') === parseFloat(item.adjustment || '0'),
                );

                // Get price
                const price = parseFloat(item.adjustment || '0');

                return (
                  <div
                    key={item.id}
                    className={`border-b p-2 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${
                      isAlreadyAdded ? 'border-l-4 border-l-green-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div
                          className="hyphens-auto break-words text-sm font-medium"
                          title={item.description}
                        >
                          {item.description}
                        </div>
                        <div className="mt-1 flex items-center justify-between">
                          <span className="text-xs text-gray-500">
                            {item.isFlatrate === '1' ? 'Flat Rate' : 'Custom'}
                          </span>
                          <span className="whitespace-nowrap font-medium">${price.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="ml-2 flex-shrink-0">
                        {isAlreadyAdded ? (
                          <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                            Added
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 bg-white text-xs"
                            onClick={() => handleAddCatalogService(item)}
                          >
                            Add
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteConfirmation.isOpen}
        onOpenChange={(open) => !open && cancelDelete()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Approved Service</AlertDialogTitle>
            <AlertDialogDescription className="break-words">
              Are you sure you want to delete the approved service &ldquo;
              <span className="overflow-wrap-anywhere break-all">
                {deleteConfirmation.serviceName}
              </span>
              &rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mobile Actions Accordion */}
      {mobileActions.isOpen && mobileActions.serviceId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-sm rounded-t-lg bg-white p-4 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Service Actions</h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={closeMobileActions}
              >
                <Cross2Icon className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {(() => {
                const service = selectedServices.find((s) => s.id === mobileActions.serviceId);
                if (!service) return null;
                const isApproved = isServiceApproved(service);

                return (
                  <>
                    {isProposalAdmin && !isApproved && (
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          handleMarkAsNonNegotiable(service);
                          closeMobileActions();
                        }}
                        disabled={markingItemId === service.id}
                      >
                        {markingItemId === service.id ? (
                          <LoadingIcon className="mr-2 h-4 w-4" width={16} height={16} />
                        ) : (
                          <Lock className="mr-2 h-4 w-4" />
                        )}
                        Non-Negotiable
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => {
                        openEditDialog(service.id);
                        closeMobileActions();
                      }}
                      disabled={isApproved}
                    >
                      <Pencil1Icon className="mr-2 h-4 w-4" />
                      Edit Service
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-red-600 hover:text-red-700"
                      onClick={() => {
                        closeMobileActions();
                        initiateDelete(service.id);
                      }}
                    >
                      <Cross2Icon className="mr-2 h-4 w-4" />
                      Delete Service
                    </Button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Unified Edit Dialog */}
      <AlertDialog
        open={editDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) closeEditDialog();
        }}
      >
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Service</AlertDialogTitle>
            <AlertDialogDescription
              className="break-words"
              style={{ wordBreak: 'break-word', hyphens: 'auto' }}
            >
              Update the details for this service item.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <FormContext {...editForm}>
            <form
              onSubmit={editForm.handleSubmit((data) => {
                const service = editDialog.service;
                if (service) {
                  saveEditedService(data, service);
                }
              })}
              className="space-y-4"
            >
              <div className="space-y-4 py-4">
                <FormField
                  control={editForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter service description..."
                          className="min-h-[120px] bg-white text-sm"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price</FormLabel>
                      <FormControl>
                        <MoneyInput
                          placeholder="0.00"
                          className="bg-white text-sm"
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numValue = value === '' ? 0 : Number(value);
                            field.onChange(numValue);
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={closeEditDialog}>Cancel</AlertDialogCancel>
                <Button type="submit" disabled={!editForm.formState.isValid}>
                  Save Changes
                </Button>
              </AlertDialogFooter>
            </form>
          </FormContext>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
